/**
 * hr-delegation domain — typed API client for HR Delegation Phase 4.
 *
 * Worker routes (src/routes/hr-delegation.js + pending-changes.js + employees.js):
 *   GET  /hr-delegation/my-capabilities  → current user's resolved grants
 *   GET  /hr-delegation/settings         → owner config (tenant tier + presets + members)
 *   POST /hr-delegation/settings         → owner save (default_tier and/or overrides)
 *   GET  /pending-changes?status=        → owner approval inbox
 *   POST /pending-changes/decide         → approve / reject
 *
 * apiFetch already unwraps the { ok, data } envelope, so helpers return the
 * inner payload directly.
 */
import { apiFetch } from '../lib/api-client';

// ── Capability model ────────────────────────────────────────────────────────
export type CapGrant = 'allow' | 'pending' | 'deny';
export type CapabilityKey =
  | 'view_basic' | 'view_comp' | 'view_audit'
  | 'edit_employee' | 'edit_salary'
  | 'config_comp' | 'config_org' | 'config_assessment'
  | 'run_comp_sim' | 'gen_doc' | 'gen_legal_doc'
  | 'manage_idp_training' | 'invite_peer' | 'invite_manager';

/** Current user's resolved HR capabilities (drives reveal buttons / conditional UI). */
export interface MyCapabilities {
  role: string;
  tier: number | null;
  grants: Record<string, CapGrant>;
  can_view_comp: boolean;
  can_reveal: boolean;
}

/** Owner Delegation-tab config: tenant tier + tier presets + per-HR overrides. */
export interface HrDelegationSettings {
  default_tier: number;
  capabilities: Array<{ key: CapabilityKey; label: string }>;
  /** keyed by "1" | "2" | "3" → full preset grant map */
  tiers: Record<string, Record<string, CapGrant>>;
  members: Array<{ user_id: string; name: string; role: string; tier: number | null }>;
}

export interface SaveDelegationBody {
  default_tier?: number;
  overrides?: Array<{ user_id: string; tier: number | null }>;
}

// ── Pending changes (owner inbox) ──────────────────────────────────────────
export type PendingStatus = 'pending' | 'approved' | 'rejected';
export type PendingOp = 'insert' | 'update' | 'delete';

export interface PendingChange {
  id: string;
  tenant_id: string;
  target_table: string;
  target_id: string | null;
  op: PendingOp;
  payload: Record<string, unknown>;
  capability: string | null;
  requested_by_user_id: string;
  status: PendingStatus;
  requested_at: string;
  decided_by_user_id: string | null;
  decided_at: string | null;
  decision_note: string | null;
}

// SoT (addendum 2026-06-02): exact-salary reveal + reveal-audit removed — Eval
// never shows an individual's exact salary (RevealAuditRow / RevealResult /
// revealAudit() / reveal() deleted).

export const hrDelegationApi = {
  myCapabilities: (): Promise<MyCapabilities> =>
    apiFetch<MyCapabilities>('/api/v1/hr-delegation/my-capabilities'),

  getSettings: (): Promise<HrDelegationSettings> =>
    apiFetch<HrDelegationSettings>('/api/v1/hr-delegation/settings'),
  saveSettings: (body: SaveDelegationBody): Promise<{ saved: boolean }> =>
    apiFetch<{ saved: boolean }>('/api/v1/hr-delegation/settings', { method: 'POST', body }),

  // Pending-changes inbox (owner)
  listPending: (status: PendingStatus = 'pending'): Promise<PendingChange[]> =>
    apiFetch<PendingChange[]>(`/api/v1/pending-changes?status=${status}`),
  decide: (id: string, decision: 'approve' | 'reject', note?: string): Promise<{ id: string; status: PendingStatus }> =>
    apiFetch<{ id: string; status: PendingStatus }>('/api/v1/pending-changes/decide', {
      method: 'POST',
      body: { id, decision, note },
    }),
};
